import cn from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import ReactPlaceholder from 'react-placeholder';
import { useSelector } from 'react-redux';
import isDesktop from 'store/app/selector/isDesktop';
import { isNewPublicAsset } from 'store/publicAssetProduct/operation/fetchPublicAssetProductTypesOperation';
import styles from './StudioProductPreviewMobile.module.scss';
import OpLightbox from '../../Common/op-ui-wrappers/OpLightbox/OpLightbox';

export enum PRODUCT_TYPES {
  POSTER = 173,
}

export interface StudioProductPreviewMobileProps {
  originalPreviews: string[];
  productTypeId: number;
  forcedPreviews: { [index: number]: string };
  imagePreview: string;
  onMainImageLoaded: (width: number, height: number) => void;
}

const StudioProductPreviewMobile: FC<StudioProductPreviewMobileProps> = ({
  originalPreviews,
  productTypeId,
  forcedPreviews,
  imagePreview,
  onMainImageLoaded,
}) => {
  const previewContainer = useRef<HTMLDivElement>(null);
  const verticalSlider = useRef(null);
  const horizontalSlider = useRef(null);
  const slideIndex = useRef(0);
  const productPreviewIndex = 0;
  const previewSorted = isNewPublicAsset()
    ? [...originalPreviews.slice(0, 1), imagePreview, ...originalPreviews.slice(1)]
    : [imagePreview, ...originalPreviews];
  const mixedPreviews = previewSorted.map((preview, index) => forcedPreviews[index] || preview);
  const isPoster = productTypeId === PRODUCT_TYPES.POSTER;
  const withGreyBg = !originalPreviews[0] && isPoster;
  const isDesktopDevice = useSelector(isDesktop);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const gotoSlide = (slideIdx: number) => {
    if (slideIndex.current === slideIdx) {
      return;
    }
    horizontalSlider.current && (horizontalSlider.current as any).slickGoTo(slideIdx);
    verticalSlider.current && (verticalSlider.current as any).slickGoTo(slideIdx);
    slideIndex.current = slideIdx;
  };

  useEffect(() => {
    // if current slide index point to not available preview, slide to first slide
    if (originalPreviews.length && slideIndex.current > originalPreviews.length - 1) {
      gotoSlide(0);
    }
  }, [originalPreviews]);

  useEffect(() => {
    gotoSlide(productPreviewIndex);
  }, [productPreviewIndex]);

  const mainImageLoaded = (e: any) => {
    onMainImageLoaded(e.target.width, e.target.height);
  };

  return (
    <div className={styles.productPreviewContainer}>
      <OpLightbox
        images={mixedPreviews.map((preview) => ({
          src: preview,
        }))}
        visible={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
      />
      <ReactPlaceholder
        showLoadingAnimation
        type="rect"
        ready={mixedPreviews.length > 0}
        className={styles.placeholder}
      >
        <>
          <div className={cn(styles.productPreviews)} ref={previewContainer}>
            {mixedPreviews.length > 0 && (
              <Slider
                ref={horizontalSlider}
                accessibility={false}
                speed={500}
                dots
                arrows={isDesktopDevice}
                slidesToShow={1}
                slidesToScroll={1}
                infinite={false}
                afterChange={(currentSlideIdx) => gotoSlide(currentSlideIdx)}
              >
                {mixedPreviews.map((preview, index) => (
                  <div
                    key={`hs_${preview}`}
                    onClick={() => setIsLightboxOpen(true)}
                    className={cn(styles.productPreview, {
                      [styles.withGreyBg]: withGreyBg,
                      [styles.withSmallerImage]: index === 0 && isPoster,
                      [styles.withShadow]: index === 0 && isPoster && withGreyBg,
                    })}
                  >
                    <img
                      className={cn({
                        [styles.containImage]: index === 0,
                        [styles.coverImage]: index !== 0,
                      })}
                      src={preview}
                      alt=" "
                      onLoad={index === 0 ? mainImageLoaded : undefined}
                    />
                  </div>
                ))}
              </Slider>
            )}
          </div>
        </>
      </ReactPlaceholder>
    </div>
  );
};

export default StudioProductPreviewMobile;
